import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import jwtDecode from 'jwt-decode';
import { catchError, filter, map, Observable, EMPTY, switchMap, take, throwError, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateManagementService } from './services/state-management.service';
import { AuthenticationService } from '../features/authentication/services/authentication.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  private isRefreshing = false; // Tracks if a refresh is in progress
  private readonly refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private stateManagementService = inject(StateManagementService);
  private router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly authenticationService = inject(AuthenticationService);

  constructor() {}

  intercept(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Exclude token refresh requests from interception
    if (httpRequest.url.includes('/refreshToken')) {
      return next.handle(httpRequest);
    }

    const token = window.localStorage.getItem('token');
    const dealer = window.localStorage.getItem('dealerId');
    const tokenExpiry = window.localStorage.getItem('token-expiry');
    const customerCode = window.localStorage.getItem('customer');
    if (this.authenticationService.isTokenExpired()) {
      sessionStorage.setItem('session-expired', 'true');
      this.clearStorageAndResetStates();
      this.router.navigate(['/authlogin']);
      return EMPTY;
    }

    const remainingTime = Number(tokenExpiry) * 1000 - Date.now();
    const tokenRefreshThresholdMillis = environment.tokenRefreshThreshold || 105 * 60 * 1000;

    const handleRequest = (authToken: string) => {
      const headers = this.setHeaders(httpRequest, authToken, dealer, customerCode);
      return next.handle(httpRequest.clone({ headers })).pipe(
        catchError((error: HttpErrorResponse) => {
          // TODO: specific error handling required.
          const isAuthError = error.status === 401 || error.status === 403;
          const isExcludedUrl =
            error.url === environment.camServiceUrl + environment.serviceUris.specifications ||
            error.url.startsWith(environment.backOfficeUrl) ||
            error.url.includes(environment.serviceUris.saveUserGroup.replace('{', '%7B').replace('}', '%7D'));
          if (isAuthError && !isExcludedUrl) {
            this.clearStorageAndResetStates();

            // Check where the error came from to determine correct return URL
            this.router.navigate(['/authlogin']);
          }
          return throwError(() => error);
        })
      );
    };

    if (remainingTime <= tokenRefreshThresholdMillis) {
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        return this.refreshToken().pipe(
          switchMap((newToken) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(newToken);
            return handleRequest(newToken);
          }),
          catchError(() => {
            this.isRefreshing = false;
            return handleRequest(token);
          })
        );
      } else {
        // Wait for the ongoing refresh to complete
        return this.refreshTokenSubject.pipe(
          filter((token) => token !== null),
          take(1),
          switchMap((newToken) => handleRequest(newToken!))
        );
      }
    }

    return handleRequest(token);
  }

  private refreshToken(): Observable<string> {
    const token = window.localStorage.getItem('token');
    const dealer = window.localStorage.getItem('dealerId');
    const refreshTokenUrl = `${environment.authUrl}${environment.serviceUris.authBase}${dealer}/refreshToken`;
    return this.http
      .post(
        refreshTokenUrl,
        {},
        {
          observe: 'response',
          headers: token ? { Authorization: token } : {}
        }
      )
      .pipe(
        map((response) => {
          const newToken = response.headers.get('Authorization');
          if (newToken) {
            window.localStorage.setItem('token', newToken);
            const decodedToken: { exp: number } = jwtDecode(newToken.replace('Bearer ', ''));
            const tokenExpiry = decodedToken.exp;
            if (tokenExpiry) {
              window.localStorage.setItem('token-expiry', tokenExpiry.toString());
            }
            return newToken;
          }
          return token;
        })
      );
  }

  private setHeaders(httpRequest: HttpRequest<any>, token: string, dealer: string, customerCode: string) {
    return httpRequest.headers.set('Authorization', token).set('dealer', dealer).set('customer', customerCode);
  }

  clearStorageAndResetStates() {
    // Keep user-specific column preferences across logout and re-login.
    const preservedPrefix = 'columnPrefs:';
    const preserved: { [key: string]: string | null } = {};
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(preservedPrefix)) {
        preserved[key] = window.localStorage.getItem(key);
      }
    }

    window.localStorage.removeItem('token');
    window.localStorage.removeItem('dealerId');
    window.localStorage.removeItem('customer');
    window.localStorage.removeItem('token-expiry');
    window.localStorage.removeItem('roles');
    window.localStorage.clear();
    Object.keys(preserved).forEach((key) => {
      const value = preserved[key];
      if (value !== null) {
        window.localStorage.setItem(key, value);
      }
    });
    this.stateManagementService.resetAllStates();
  }
}
